import { Search, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Label, type MultiSelectOption, SearchableMultiSelect } from "~/components/ui";

import { useEvidenceModelContext } from "~/features/evidences/hooks";
import type { EvidenceListFilters } from "~/features/evidences/types";
import { EvidenceUserSelect } from "./EvidenceUserSelect";

interface EvidencesSearchFormProps {
  filters: EvidenceListFilters;
  onApply: (next: EvidenceListFilters) => void;
}

export function EvidencesSearchForm({ filters, onApply }: EvidencesSearchFormProps) {
  const { t } = useTranslation();
  const { references } = useEvidenceModelContext();

  const [referenceIds, setReferenceIds] = useState<string[]>(filters.referenceIds);
  const [userId, setUserId] = useState<string | undefined>(filters.userId);

  // Re-seed the draft whenever committed URL filters change from outside the form (back/forward,
  // shared link). `filters` is a stable useMemo reference in the container.
  const prevFiltersRef = useRef(filters);
  if (prevFiltersRef.current !== filters) {
    prevFiltersRef.current = filters;
    setReferenceIds(filters.referenceIds);
    setUserId(filters.userId);
  }

  const referenceOptions = useMemo<MultiSelectOption[]>(
    () => references.map((r) => ({ id: r.id, label: r.shortCode ? `${r.shortCode} — ${r.title}` : r.title })),
    [references],
  );

  return (
    <form
      className="flex flex-col gap-5 text-[13px]"
      onSubmit={(e) => {
        e.preventDefault();
        onApply({ referenceIds, userId });
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.reference.modelNames")}</Label>
        <SearchableMultiSelect
          placeholder={t("model.reference.modelNames")}
          searchPlaceholder={t("ui.search")}
          options={referenceOptions}
          selectedIds={referenceIds}
          onChange={setReferenceIds}
          emptyText={t("feature.common.noData")}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-12-bold capitalize text-fg-2">{t("model.user.modelName")}</Label>
        <EvidenceUserSelect selectedId={userId} onChange={setUserId} />
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm">
          <Search />
          <span>{t("ui.search")}</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setReferenceIds([]);
            setUserId(undefined);
            onApply({ referenceIds: [] });
          }}
        >
          <X />
          <span>{t("ui.reset")}</span>
        </Button>
      </div>
    </form>
  );
}
